import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import api from './api';
import { useAuth } from './Auth';
import { Plan, PlanLimits, Subscription, UserProfile } from '../models';
import {
  canAccessStores,
  canCreateLinkProfile,
  canUseCustomButtonColors,
  hasAnalytics,
} from '../utils/planUtils';

interface PlanAccessContextValue {
  loading: boolean;
  isSuperuser: boolean;
  subscription: Subscription | null;
  plan: Plan | null;
  limits: PlanLimits | null;
  canAccessStores: boolean;
  hasAnalytics: boolean;
  canUseCustomButtonColors: boolean;
  canCreateLinkProfile: (currentCount: number) => boolean;
  refresh: () => Promise<void>;
}

const PlanAccessContext = createContext<PlanAccessContextValue | undefined>(
  undefined
);

interface PlanAccessProviderProps {
  children: React.ReactNode;
}

export const PlanAccessProvider: React.FC<PlanAccessProviderProps> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isSuperuser, setIsSuperuser] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      setIsSuperuser(false);
      return;
    }

    setLoading(true);
    try {
      const [subscriptionResponse, profileResponse] = await Promise.all([
        api.get<Subscription>('/v1/subscription/'),
        api.get<UserProfile>('/v1/profile/'),
      ]);
      setSubscription(subscriptionResponse.data);
      setIsSuperuser(Boolean(profileResponse.data?.is_superuser));
    } catch (error) {
      console.error('Failed to load subscription', error);
      setSubscription(null);
      setIsSuperuser(false);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const plan = subscription?.plan ?? null;
  const limits = isSuperuser ? null : plan?.limits ?? null;

  const value = useMemo<PlanAccessContextValue>(
    () => ({
      loading,
      isSuperuser,
      subscription,
      plan,
      limits,
      // Superusers bypass all subscription limits (matches backend get_user_limits).
      canAccessStores: isSuperuser || canAccessStores(plan),
      hasAnalytics: isSuperuser || hasAnalytics(plan),
      canUseCustomButtonColors:
        isSuperuser || canUseCustomButtonColors(plan),
      canCreateLinkProfile: (currentCount: number) =>
        isSuperuser || canCreateLinkProfile(plan, currentCount),
      refresh,
    }),
    [loading, isSuperuser, subscription, plan, limits, refresh]
  );

  return (
    <PlanAccessContext.Provider value={value}>
      {children}
    </PlanAccessContext.Provider>
  );
};

export function usePlanAccess(): PlanAccessContextValue {
  const context = useContext(PlanAccessContext);
  if (context === undefined) {
    throw new Error('usePlanAccess must be used within a PlanAccessProvider');
  }
  return context;
}
